import React, { useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useFindMenu, useTrans, useTealium } from '@next-nx/hooks'
import { getTimestamp } from '@next-nx/utils'
import {
  CategoriaType,
  ListingType,
  PaginaType,
  CategoriaVeicoloType,
  MarcaVeicoloType,
} from '@next-nx/utils/types'
import { Breadcrumbs, WContainer, MainTitle } from '@next-nx/shared-ui/atoms'
import { CircleBox } from '@next-nx/shared-ui/molecules'
import {
  Page,
  CompareBar,
  ListingSeoText,
  CategorieSection,
  BrandsSection,
  HelpBanner,
} from '@next-nx/shared-ui/organisms'
import { getCategoriaUrlParam, getOffertaUrlParams } from '@next-nx/utils'
import {
  CATEGORIA_MOBILITA_SOSTENIBILE_CODICE,
  CATEGORIA_TIPO_LISTING_PRIMARIO,
} from '@next-nx/utils/settings'
import styles from './ListingTemplate.module.sass'
import ProdottiListing from '../../organisms/ProdottiListing/ProdottiListing'

interface Props {
  pagina: PaginaType
  categoria: CategoriaType
  filtro_categoria: string
  listing: ListingType
  categorieVeicoli?: CategoriaVeicoloType[]
  marcheVeicoli?: MarcaVeicoloType[]
}

const ListingTemplate = (props: Props) => {
  const { pagina, categoria, filtro_categoria, listing, categorieVeicoli, marcheVeicoli } = props

  const t = useTrans()

  const listingMenu = useFindMenu('pagina-listing')
  const categoriaMenu = useFindMenu(categoria.codice, listingMenu)

  const helpBannerMenu = useFindMenu('help_banner')
  const helpBannerListingMenu = useFindMenu('listing-pdp', helpBannerMenu)

  const [compareBarCollapsed, setCompareBarCollapsed] = useState(false) // Da istruzioni: al primo atterraggio visibile

  const { linkDataLayer } = useTealium()

  const handleTracking = (action_type: string = '', c?: string, t?: string, cta?: string) => {
    const action_timestamp = getTimestamp()

    let variant = ''
    if (cta?.toLowerCase().includes('incluso')) {
      cta = 'vai_al_catalogo_tutto_incluso'
      variant = 'cataloghi'
    } else if (cta?.toLowerCase().includes('anticipo')) {
      cta = 'vai_al_catalogo_anticipo_zero'
      variant = 'cataloghi'
    } else if (cta?.toLowerCase().includes('hybrid')) {
      cta = 'scopri_la_gamma_hybrid'
      variant = 'gamma'
    } else if (cta?.toLowerCase().includes('electric')) {
      cta = 'scopri_la_gamma_full_electric'
      variant = 'gamma'
    }

    linkDataLayer({
      virtual: 'action',
      action_name: 'navigazione',
      action_tech: 'click',
      action_type: cta?.replace(/ /g, '_').toLowerCase(),
      action_timestamp,
      action_effect: `interazione_componente_${variant}`,
    })
  }

  return (
    <Page selfCanonical={true} pagina={pagina} item={categoria} className={`${styles.root}`}>
      <WContainer>
        <Breadcrumbs breadcrumbs={pagina.breadcrumbs} className={styles.breadcrumbs} />
        {!!categoria.nome && (
          <MainTitle
            label={
              categoria.tipo === CATEGORIA_TIPO_LISTING_PRIMARIO
                ? `${t('Soluzioni per')} ${categoria.nome}`
                : categoria.nome
            }
            className={styles.title}
          />
        )}
        {categoria.codice === CATEGORIA_MOBILITA_SOSTENIBILE_CODICE &&
          categoriaMenu?.children &&
          categoriaMenu.children?.edges.length > 0 && (
            <div className={styles.topBoxes}>
              <Swiper
                slidesPerView={1.2}
                spaceBetween={30}
                breakpoints={{
                  768: {
                    slidesPerView: 2,
                  },
                }}
              >
                {categoriaMenu.children.edges.map((x, i) => (
                  <SwiperSlide key={x.node.pk}>
                    <CircleBox
                      item={{
                        ...x.node,
                        url: x.node.url,
                      }}
                      animationVariant={i % 2 === 0 ? 'vertical' : 'horizontal'}
                      handleTracking={() => handleTracking('', '', '', x.node.name)}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}
        <ProdottiListing
          listing={listing}
          categoria={categoria}
          filtro_categoria={filtro_categoria}
        />
      </WContainer>
      {!!categoria.descrizione && (
        <ListingSeoText text={categoria.descrizione} className={styles.seoText} />
      )}
      {categoria.codice !== CATEGORIA_MOBILITA_SOSTENIBILE_CODICE &&
        categoriaMenu?.children &&
        categoriaMenu.children?.edges.length > 0 && (
          <WContainer>
            <div className={styles.proposte}>
              {categoriaMenu.children.edges.map((x, i) => (
                <CircleBox
                  item={{
                    ...x.node,
                    url: x.node.url,
                  }}
                  animationVariant={i % 2 === 0 ? 'vertical' : 'horizontal'}
                  key={x.node.pk}
                  handleTracking={() => handleTracking('', '', '', x.node.name)}
                />
              ))}
            </div>
          </WContainer>
        )}
      {categorieVeicoli && categorieVeicoli.length > 0 && (
        <CategorieSection
          titolo={t('Le nostre categorie')}
          categorie={categorieVeicoli}
          className={styles.categories}
        />
      )}
      {marcheVeicoli && marcheVeicoli.length > 0 && (
        <BrandsSection
          titolo={t('I nostri marchi')}
          ctaLabel={t('Vai a tutti i marchi')}
          url="/marchi"
          brands={marcheVeicoli}
          className={styles.brands}
        />
      )}
      {helpBannerListingMenu && (
        <HelpBanner item={helpBannerListingMenu} className={styles.helpBanner} />
      )}
      <CompareBar
        type="plp"
        collapsed={compareBarCollapsed}
        setCollapsed={(v) => setCompareBarCollapsed(v)}
        url={'/comparatore' + getCategoriaUrlParam(categoria.codice)}
        className={styles.compareBar}
      />
    </Page>
  )
}

export default ListingTemplate
